import { memo, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasFlexibleLeaseLengths,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLessThanOneYearLeaseLengths,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMonthlyLeaseLengths,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMoreThanOneYearLeaseLengths,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasNoneLeaseLengths,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOneYearLeaseLengths,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSixMonthsLeaseLengths,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';
import { AMENITY_SAVE_ERROR_MESSAGE } from '@zg-rentals/zillow-rental-manager/constants';

import { amenityPatches } from '../../../../hooks/queries/listingDetailsCache';
import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { useFeedAmenityMutation } from '../../../../hooks/useFeedAmenityMutation';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { type AmenityFlagSave, getIsInputFormDisabled } from '../../utils';

import {
  LEASE_LENGTH_METADATA,
  LEASE_LENGTH_TYPE_KEYS,
  type LeaseLengthKey,
} from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<LeaseLengthKey, boolean>;

const NONE_KEY: LeaseLengthKey = 'hasNone';

const LeaseLengthAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const leaseLengths = listingDetails?.buildingAttributes?.leaseLengths;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasFlexible: leaseLengths?.hasFlexible ?? false,
    hasMonthly: leaseLengths?.hasMonthly ?? false,
    hasSixMonths: leaseLengths?.hasSixMonths ?? false,
    hasOneYear: leaseLengths?.hasOneYear ?? false,
    hasLessThanOneYear: leaseLengths?.hasLessThanOneYear ?? false,
    hasMoreThanOneYear: leaseLengths?.hasMoreThanOneYear ?? false,
    hasNone: leaseLengths?.hasNone ?? false,
  });

  // Re-seed from listing details when they change (e.g. after a feed override is turned off and the reimport
  // restores the feed values), so the checkboxes don't keep showing the removed overrides.
  useEffect(() => {
    setLocalState({
      hasFlexible: leaseLengths?.hasFlexible ?? false,
      hasMonthly: leaseLengths?.hasMonthly ?? false,
      hasSixMonths: leaseLengths?.hasSixMonths ?? false,
      hasOneYear: leaseLengths?.hasOneYear ?? false,
      hasLessThanOneYear: leaseLengths?.hasLessThanOneYear ?? false,
      hasMoreThanOneYear: leaseLengths?.hasMoreThanOneYear ?? false,
      hasNone: leaseLengths?.hasNone ?? false,
    });
  }, [leaseLengths]);

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Lease lengths updated',
  });

  // TODO RECE-2257: feed overrides for this field don't reach the public listing yet, so it stays read-only on feed
  // listings until they do.
  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);
  const { isFeedListing, setFeedAmenity } = useFeedAmenityMutation();

  const saveAmenity = (key: keyof typeof LEASE_LENGTH_METADATA, checked: boolean) =>
    isFeedListing
      ? setFeedAmenity(LEASE_LENGTH_METADATA[key].apiKey, checked)
      : mutationMap[key]({ postData: { [LEASE_LENGTH_METADATA[key].apiKey]: checked, listingAlias } });

  const { mutateAsync: updateFlexible } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasFlexibleLeaseLengths,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasFlexibleLeaseLengths',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateMonthly } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMonthlyLeaseLengths,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMonthlyLeaseLengths',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateSixMonths } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSixMonthsLeaseLengths,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSixMonthsLeaseLengths',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateOneYear } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOneYearLeaseLengths,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOneYearLeaseLengths',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateLessThanOneYear } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLessThanOneYearLeaseLengths,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLessThanOneYearLeaseLengths',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateMoreThanOneYear } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMoreThanOneYearLeaseLengths,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasMoreThanOneYearLeaseLengths',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateNone } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasNoneLeaseLengths,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasNoneLeaseLengths',
        details: { listingAlias },
      },
    },
  });

  const mutationMap = {
    hasFlexible: updateFlexible,
    hasMonthly: updateMonthly,
    hasSixMonths: updateSixMonths,
    hasOneYear: updateOneYear,
    hasLessThanOneYear: updateLessThanOneYear,
    hasMoreThanOneYear: updateMoreThanOneYear,
    hasNone: updateNone,
  } as unknown as Record<keyof LocalState, AmenityFlagSave>;

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as LeaseLengthKey;
    const checked = event.target.value === 'true';

    startRequest();

    // Special case: clicking "None" should deselect all others
    if (fieldName === NONE_KEY && checked) {
      const previousState = { ...localState };
      const newState = LEASE_LENGTH_TYPE_KEYS.reduce(
        (acc, key) => ({ ...acc, [key]: key === NONE_KEY }),
        {} as Record<LeaseLengthKey, boolean>,
      );
      setLocalState(newState);

      try {
        // Set "None" to true
        await saveAmenity(NONE_KEY, true);

        // Set all others to false
        await Promise.all(LEASE_LENGTH_TYPE_KEYS.filter((k) => k !== NONE_KEY).map((key) => saveAmenity(key, false)));

        handleSuccess();
      } catch (err) {
        handleError();
        setLocalState(previousState);
        const errorMessage = DatarouterError.getDisplayMessage(err, AMENITY_SAVE_ERROR_MESSAGE);
        enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
      }
      return;
    }

    // Special case: clicking any other option should deselect "None"
    const previousValue = !checked;
    const previousNoneValue = localState.hasNone;
    setLocalState((prev) => ({
      ...prev,
      [fieldName]: checked,
      hasNone: false, // Always deselect "None" when selecting anything else
    }));

    try {
      // Update the clicked field
      await saveAmenity(fieldName, checked);

      // If "None" was previously selected, also update it to false
      if (previousNoneValue) {
        await saveAmenity(NONE_KEY, false);
      }

      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue, hasNone: previousNoneValue }));
      const errorMessage = DatarouterError.getDisplayMessage(err, AMENITY_SAVE_ERROR_MESSAGE);
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <Box data-testid="LeaseLengthAmenities" css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
      <FieldSet legend={<Legend>Lease length</Legend>} disabled={isInputFormDisabled}>
        <Box css={{ display: 'flex', flexDirection: 'row', gap: '400', flexWrap: 'wrap' }}>
          <Box css={{ display: 'flex', flexDirection: 'column', flex: '1 0 280px' }}>
            {LEASE_LENGTH_TYPE_KEYS.slice(0, Math.floor((LEASE_LENGTH_TYPE_KEYS.length + 1) / 2)).map((fieldName) => {
              const metadata = LEASE_LENGTH_METADATA[fieldName];
              return (
                <LabeledControl
                  key={`leaseLengths-${metadata.label}`}
                  label={<Label>{metadata.label}</Label>}
                  control={
                    <Checkbox
                      name={fieldName}
                      value={String(!localState[fieldName])}
                      checked={localState[fieldName]}
                      onChange={handleCheckboxChange}
                    />
                  }
                />
              );
            })}
          </Box>

          <Box css={{ display: 'flex', flexDirection: 'column', flex: '1 0 280px' }}>
            {LEASE_LENGTH_TYPE_KEYS.slice(Math.floor((LEASE_LENGTH_TYPE_KEYS.length + 1) / 2)).map(
              (fieldName, index) => {
                const metadata = LEASE_LENGTH_METADATA[fieldName];
                return (
                  <LabeledControl
                    key={`leaseLengths-${Math.floor((LEASE_LENGTH_TYPE_KEYS.length + 1) / 2) + index}`}
                    label={<Label>{metadata.label}</Label>}
                    control={
                      <Checkbox
                        name={fieldName}
                        value={String(!localState[fieldName])}
                        checked={localState[fieldName]}
                        onChange={handleCheckboxChange}
                      />
                    }
                  />
                );
              },
            )}
          </Box>
        </Box>
      </FieldSet>
    </Box>
  );
};

export default memo(LeaseLengthAmenities);
